import { cn } from '@/lib/cn';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import {
  BetweenHorizontalStart,
  Book,
  ChevronRight,
  MoreHorizontal,
  Replace,
} from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';
import {
  ACTION_ROW_CLASS,
  type SelectionEditorActions,
} from './selection-ai-actions.utils';
import { useTrackMenuClick } from './use-track-menu-click';

const MORE_OPTIONS_TRACKING: Partial<
  Record<'find_replace' | 'create_snippet', { action: ButtonAction; label: string }>
> = {
  find_replace: { action: ButtonAction.TextMenuFindReplace, label: 'find_replace' },
  create_snippet: { action: ButtonAction.TextMenuCreateSnippet, label: 'create_snippet' },
};

/**
 * v5.2 "More options" submenu row for the selection → Ask Heidi menu. Collapses
 * the note editor actions (find & replace / create snippet / convert to
 * section) into a tree view that opens to the side, mirroring the Rewrite
 * submenu it sits beside. Rendered as the last item before "Ask anything".
 *
 * Uses raw Radix (same pattern as the sibling Rewrite submenu) with NO Portal:
 * the flyout renders inline so Radix anchors it to the trigger's rect and pops
 * out beside the parent row (a body Portal in this tippy-hosted menu loses the
 * anchor and snaps to the viewport's top-left). The whole AI-actions bubble
 * menu is appended to <body> (see `text-menu-base` appendTo), so this inline
 * flyout still paints above the side panel; `avoidCollisions` flips it to the
 * left when there's no room on the right. Styling mirrors the design-system
 * DropdownMenuContent / DropdownMenuItem so padding, gap, ring and shadow stay
 * consistent. The `onMouseDown` guard keeps the editor selection alive through
 * the interaction.
 *
 * Renders nothing when no action is enabled, so the caller only needs to gate
 * on the variant + handler presence.
 */
export const SelectionMoreOptionsSubmenu = ({
  editorActions,
}: {
  editorActions: SelectionEditorActions;
}) => {
  const intl = useIntl();
  const trackClick = useTrackMenuClick();
  const [isOpen, setIsOpen] = useState(false);

  const items = [
    editorActions.enableFindReplace && {
      id: 'find_replace',
      icon: Replace,
      message: selectionAiActionsMessages.findReplace,
      onSelect: editorActions.onFindReplace,
    },
    editorActions.enableSnippet && {
      id: 'create_snippet',
      icon: Book,
      message: selectionAiActionsMessages.createSnippet,
      onSelect: editorActions.onCreateSnippet,
    },
    editorActions.enableConvertToSection && {
      id: 'convert_to_section',
      icon: BetweenHorizontalStart,
      message: selectionAiActionsMessages.convertToSection,
      onSelect: editorActions.onConvertToSection,
    },
  ].filter(Boolean as unknown as <T>(value: T | false) => value is T);

  if (items.length === 0) return null;

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          className={cn(
            ACTION_ROW_CLASS,
            // Keep the row highlighted while its submenu is open (focus moves
            // into the submenu, so hover/focus alone would drop it).
            'data-[state=open]:bg-surface-hover data-[state=open]:text-text-primary'
          )}
          data-testid="selection-ai-more-options-trigger"
        >
          <MoreHorizontal size={14} className="shrink-0 text-text-secondary" />
          <span className="flex-1">
            {intl.formatMessage(selectionAiActionsMessages.moreOptions)}
          </span>
          <ChevronRight size={14} className="shrink-0 text-text-secondary" />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        // Keep the editor selection alive while interacting with the flyout.
        onMouseDown={(e) => e.preventDefault()}
        className="z-[60] flex min-w-32 flex-col rounded-lg border-0 bg-surface-elevated p-1 text-text-primary shadow-popover ring-1 ring-border"
      >
        {items.map(({ id, icon: Icon, message, onSelect }) => (
          <Dropdown.Item key={id} asChild>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                const tracking =
                  MORE_OPTIONS_TRACKING[id as keyof typeof MORE_OPTIONS_TRACKING];
                if (tracking) trackClick(tracking.action, tracking.label);
                onSelect();
              }}
              className={ACTION_ROW_CLASS}
              data-testid={`selection-ai-more-options-${id}`}
            >
              <Icon className="shrink-0 text-text-secondary" />
              {intl.formatMessage(message)}
            </button>
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};
